import { useEffect, useState } from "react";
import { NavLink, useNavigate, Outlet } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import {
  LayoutDashboard, Users, Contact, ListChecks, Settings, ScrollText,
  LogOut, Bell, Menu, X, Landmark, LayoutTemplate, FormInput, Mails, MessageSquareText, PhoneCall, RotateCcw, Video, Newspaper,
} from "lucide-react";
import { Button } from "@/components/ui/button";

const NAV = [
  { to: "/admin", label: "Dashboard", icon: LayoutDashboard, end: true, testid: "nav-dashboard" },
  { to: "/admin/leads", label: "Leads", icon: Contact, testid: "nav-leads" },
  { to: "/admin/employees", label: "Employees", icon: Users, testid: "nav-employees" },
  { to: "/admin/landing", label: "Landing Page", icon: LayoutTemplate, testid: "nav-landing" },
  { to: "/admin/form", label: "Lead Form", icon: FormInput, testid: "nav-form" },
  { to: "/admin/email", label: "Email Automation", icon: Mails, testid: "nav-email" },
  { to: "/admin/ai", label: "AI Calling", icon: PhoneCall, testid: "nav-ai" },
  { to: "/admin/reactivation", label: "Lead Reactivation", icon: RotateCcw, testid: "nav-reactivation" },
  { to: "/admin/webinars", label: "Webinars", icon: Video, testid: "nav-webinars" },
  { to: "/admin/seo", label: "SEO Blog", icon: Newspaper, testid: "nav-seo" },
  { to: "/admin/whatsapp", label: "WhatsApp Templates", icon: MessageSquareText, testid: "nav-whatsapp" },
  { to: "/admin/statuses", label: "Lead Statuses", icon: ListChecks, testid: "nav-statuses" },
  { to: "/admin/settings", label: "Settings", icon: Settings, testid: "nav-settings" },
  { to: "/admin/audit", label: "Audit Logs", icon: ScrollText, testid: "nav-audit" },
];

export default function AdminLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [unread, setUnread] = useState(0);

  useEffect(() => {
    api.get("/notifications/unread-count").then((r) => setUnread(r.data.count)).catch(() => {});
  }, []);

  const doLogout = async () => {
    await logout();
    navigate("/login");
  };

  return (
    <div className="app-bg min-h-screen">
      {/* Sidebar */}
      <aside
        className={`fixed z-40 h-full w-64 glass border-r border-slate-800 transition-transform lg:translate-x-0 ${
          open ? "translate-x-0" : "-translate-x-full"
        }`}
      >
        <div className="flex items-center gap-2 px-5 py-5 border-b border-slate-800">
          <div className="h-9 w-9 rounded-lg bg-primary/15 flex items-center justify-center">
            <Landmark className="h-5 w-5 text-primary" />
          </div>
          <div>
            <div className="font-heading font-extrabold tracking-tight text-lg leading-none">Paisapaid</div>
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Loan Agency CRM</div>
          </div>
        </div>
        <nav className="p-3 space-y-1">
          {NAV.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.end}
              data-testid={n.testid}
              onClick={() => setOpen(false)}
              className={({ isActive }) =>
                `flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors ${
                  isActive
                    ? "bg-primary/15 text-primary"
                    : "text-muted-foreground hover:bg-slate-800/60 hover:text-foreground"
                }`
              }
            >
              <n.icon className="h-4 w-4" />
              {n.label}
            </NavLink>
          ))}
        </nav>
      </aside>

      {/* Main */}
      <div className="lg:pl-64">
        <header className="sticky top-0 z-30 glass border-b border-slate-800 px-4 sm:px-6 py-3 flex items-center justify-between">
          <button className="lg:hidden text-foreground" onClick={() => setOpen(!open)} data-testid="sidebar-toggle">
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
          <div className="hidden sm:block text-sm text-muted-foreground">
            Welcome back, <span className="text-foreground font-semibold">{user?.name}</span>
          </div>
          <div className="flex items-center gap-3">
            <span className="rounded-full bg-accent/15 text-accent border border-accent/30 px-2.5 py-0.5 text-[11px] font-semibold uppercase tracking-wide">
              Admin
            </span>
            <div className="relative">
              <Bell className="h-5 w-5 text-muted-foreground" data-testid="notification-bell" />
              {unread > 0 && (
                <span className="absolute -top-1.5 -right-1.5 h-4 min-w-4 px-1 rounded-full bg-primary text-[10px] font-bold text-primary-foreground flex items-center justify-center">
                  {unread}
                </span>
              )}
            </div>
            <Button variant="ghost" size="sm" onClick={doLogout} data-testid="logout-button" className="text-muted-foreground hover:text-foreground">
              <LogOut className="h-4 w-4 mr-1" /> Logout
            </Button>
          </div>
        </header>
        <main className="p-4 sm:p-6 max-w-[1500px] mx-auto">
          <Outlet />
        </main>
      </div>
      {open && <div className="fixed inset-0 bg-black/50 z-30 lg:hidden" onClick={() => setOpen(false)} />}
    </div>
  );
}
